import {useState, useEffect} from "react";

import {Amplify} from "aws-amplify";
import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  getCurrentUser,
} from "aws-amplify/auth";
import {sessionStorage} from "aws-amplify/utils";
import {cognitoUserPoolsTokenProvider} from "aws-amplify/auth/cognito";

import {useForm} from "./components/Hooks/form-hook";

import styles from "./App.module.css";

import Input from "./components/UI/Input";
import Button from "./components/UI/Button";

import {
  VALIDATOR_REQUIRE,
  VALIDATOR_EMAIL,
} from "./components/UI/util/validators";

import Dashboard from "./components/Dashboard/Dashboard";

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolClientId: process.env.REACT_APP_AWS_USER_POOL_WEB_CLIENT_ID,
      userPoolId: process.env.REACT_APP_AWS_USER_POOL_ID,
    },
  },
});

function App() {
  const [formState, inputHandler] = useForm(
    {
      firstname: {
        value: "",
        isValid: false,
      },
      lastname: {
        value: "",
        isValid: false,
      },
      email: {
        value: "",
        isValid: false,
      },
      password: {
        value: "",
        isValid: false,
      },
      confirm_password: {
        value: "",
        isValid: false,
      },
    },
    false
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [confirmationFormState, confirmationInputHandler] = useForm(
    {
      email: {
        value: "",
        isValid: false,
      },
      confirmationCode: {
        value: "",
        isValid: false,
      },
    },
    false
  );

  const [loginFormState, loginInputHandler] = useForm(
    {
      email: {
        value: "",
        isValid: false,
      },
      password: {
        value: "",
        isValid: false,
      },
    },
    false
  );

  const [loginState, setLoginState] = useState({
    state: null,
    message: null,
  });

  const handleSignUp = async () => {
    console.log(`Firstname: ${formState.inputs.firstname.value}`);
    console.log(`Lastname: ${formState.inputs.lastname.value}`);
    console.log(`Email: ${formState.inputs.email.value}`);
    console.log(`Password: ${formState.inputs.password.value}`);
    console.log(`Confirm Password: ${formState.inputs.confirm_password.value}`);

    if (
      formState.inputs.password.value !==
      formState.inputs.confirm_password.value
    ) {
      setLoginState({
        state: "signup",
        message: "Passwords do not match.",
      });
      return;
    }

    setIsActionLoading(true);
    try {
      const {isSignUpComplete, userId, nextStep} = await signUp({
        username: formState.inputs.email.value,
        password: formState.inputs.password.value,
        options: {
          userAttributes: {
            given_name: formState.inputs.firstname.value,
            family_name: formState.inputs.lastname.value,
          },
          autoSignIn: true,
        },
      });

      console.log(`User Id: ${userId}`);
      console.log(`Next Step: ${JSON.stringify(nextStep)}`);
      console.log(`Is Signup Complete: ${isSignUpComplete}`);

      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        setLoginState({
          state: "confirm",
          message: `Confirm Email. Check you email for the confirmation code.`,
        });
        inputHandler("firstname", "", false);
        inputHandler("lastname", "", false);
        inputHandler("email", "", false);
        inputHandler("password", "", false);
        inputHandler("confirm_password", "", false);
      }
      setIsActionLoading(false);
    } catch (error) {
      console.log("error signing up:", error.message);
      if (
        error.message.includes(
          "Password did not conform with policy: Password must have symbol characters"
        )
      ) {
        setLoginState({
          state: "signup",
          message:
            "Password did not conform with policy: Password must have symbol characters",
        });
      } else if (error.message.includes("User already exists")) {
        setLoginState({
          state: "signup",
          message: "User already exists",
        });
      } else {
        setLoginState({
          state: "signup",
          message: "Unknown error occurred while signing up. Please try again.",
        });
      }
      setIsActionLoading(false);
    }
  };

  const handleSignUpConfirmation = async () => {
    console.log(`Email: ${confirmationFormState.inputs.email.value}`);
    console.log(
      `Confirmation Code: ${confirmationFormState.inputs.confirmationCode.value}`
    );

    setIsActionLoading(true);
    try {
      const {isSignUpComplete, nextStep} = await confirmSignUp({
        username: confirmationFormState.inputs.email.value,
        confirmationCode: confirmationFormState.inputs.confirmationCode.value,
      });
      console.log(`Is Signup Complete: ${isSignUpComplete}`);
      console.log(`Next Step: ${JSON.stringify(nextStep)}`);
      if (
        nextStep.signUpStep === "DONE" ||
        nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN"
      ) {
        setLoginState({
          state: "login",
        });
        confirmationInputHandler("email", "", false);
        confirmationInputHandler("confirmationCode", "", false);
      }
      setIsActionLoading(false);
    } catch (error) {
      console.log("error confirming sign up", error.message);
      if (
        error.message ===
        "Invalid verification code provided, please try again."
      ) {
        setLoginState({
          state: "confirm",
          message: "Invalid verification code provided, please try again.",
        });
      }
      setIsActionLoading(false);
    }
  };

  const handleLogin = async () => {
    setIsActionLoading(true);
    try {
      const {isSignedIn, nextStep} = await signIn({
        username: loginFormState.inputs.email.value,
        password: loginFormState.inputs.password.value,
      });

      console.log(`isSignedIn: ${isSignedIn}`);
      console.log(`Next Step: ${JSON.stringify(nextStep)}`);
      if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
        setLoginState({
          state: "confirm",
          message: `Email not confirmed. Check you email for the confirmation code.`,
        });
      }
      if (nextStep.signInStep === "DONE") {
        cognitoUserPoolsTokenProvider.setKeyValueStorage(sessionStorage);
        setLoginState({
          state: "logged_in",
        });
      }
      loginInputHandler("email", "", false);
      loginInputHandler("password", "", false);
      setIsActionLoading(false);
    } catch (error) {
      console.log("error signing in", error.message);
      if (error.message === "Incorrect username or password.") {
        setLoginState({
          state: "login",
          message: "Incorrect username or password.",
        });
      }
      setIsActionLoading(false);
    }
  };

  const currentAuthenticatedUser = async () => {
    setIsLoading(true);
    try {
      const {username, userId, signInDetails} = await getCurrentUser();
      console.log(`The username: ${username}`);
      console.log(`The userId: ${userId}`);
      console.log(`The signInDetails: ${JSON.stringify(signInDetails)}`);
      if (signInDetails.loginId) {
        setLoginState({
          state: "logged_in",
        });
      } else {
        setLoginState({
          state: "login",
        });
      }
      setIsLoading(false);
    } catch (err) {
      console.log(`Error getting current user: ${err.message}`);
      setLoginState({
        state: "login",
      });
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    setIsLoading(true);
    try {
      await signOut();

      setLoginState({
        state: "login",
        message: null,
      });
      setIsLoading(false);
    } catch (error) {
      console.log("error signing out: ", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    currentAuthenticatedUser();
  }, []);

  return (
    <div className={`${styles.App}`}>
      <header className={`${styles.App__Header}`}>
        <div className={`${styles.App__Header__Left}`}>
          <div className={`${styles.App__Header__Logo}`}>
            <img src="./images/ccx_logo.png" alt="ccx logo" />
          </div>
        </div>

        {loginState.state === "logged_in" && (
          <div className={`${styles.App__Login__Button}`}>
            <Button onClick={handleSignOut}>LOG OUT</Button>
          </div>
        )}
      </header>
      <section className={`${styles.App__Section}`}>
        {loginState.state === "signup" && !isLoading && (
          <div className={`${styles.App__Login}`}>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="firstname"
                element="input"
                type="text"
                placeholder="First Name"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                value={formState.inputs.firstname.value}
                valid={formState.inputs.firstname.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="lastname"
                element="input"
                type="text"
                placeholder="Last Name"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                value={formState.inputs.lastname.value}
                valid={formState.inputs.lastname.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="email"
                element="input"
                type="text"
                placeholder="Email"
                variant="primary"
                validators={[VALIDATOR_REQUIRE(), VALIDATOR_EMAIL()]}
                onInput={inputHandler}
                value={formState.inputs.email.value}
                valid={formState.inputs.email.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="password"
                element="input"
                type="password"
                placeholder="Password"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                value={formState.inputs.password.value}
                valid={formState.inputs.password.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="confirm_password"
                element="input"
                type="password"
                placeholder="Confirm Password"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={inputHandler}
                value={formState.inputs.confirm_password.value}
                valid={formState.inputs.confirm_password.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <div className={`${styles.App__Login__Button}`}>
                <Button onClick={handleSignUp} disabled={!formState.isValid}>
                  {isActionLoading === true ? (
                    <img
                      style={{width: "20px", height: "20px"}}
                      src="./images/three-dots.svg"
                      alt="Loading..."
                    />
                  ) : (
                    "SIGN UP"
                  )}
                </Button>
              </div>
              <div className={`${styles.App__Login__Button}`}>
                <Button
                  onClick={() =>
                    setLoginState({
                      state: "login",
                      message: null,
                    })
                  }
                >
                  LOGIN
                </Button>
              </div>
            </div>

            {loginState.message !== null && (
              <div className={`${styles.App__Login__Segment}`}>
                <h6>{loginState.message}</h6>
              </div>
            )}

            <div className={`${styles.App__Login__Segment}`}>
              <h5
                onClick={() =>
                  setLoginState({
                    state: "confirm",
                    message: null,
                  })
                }
              >
                Already have a confirmation code? Confirm your account
              </h5>
            </div>
          </div>
        )}

        {loginState.state === "login" && !isLoading && (
          <div className={`${styles.App__Login}`}>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="email"
                element="input"
                type="text"
                placeholder="Email"
                variant="primary"
                validators={[VALIDATOR_REQUIRE(), VALIDATOR_EMAIL()]}
                onInput={loginInputHandler}
                value={loginFormState.inputs.email.value}
                valid={loginFormState.inputs.email.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="password"
                element="input"
                type="password"
                placeholder="Password"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={loginInputHandler}
                value={loginFormState.inputs.password.value}
                valid={loginFormState.inputs.password.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <div className={`${styles.App__Login__Button}`}>
                <Button
                  onClick={handleLogin}
                  disabled={!loginFormState.isValid}
                >
                  {isActionLoading === true ? (
                    <img
                      style={{width: "20px", height: "20px"}}
                      src="./images/three-dots.svg"
                      alt="Loading..."
                    />
                  ) : (
                    "LOGIN"
                  )}
                </Button>
              </div>
            </div>

            {loginState.message !== null && (
              <div className={`${styles.App__Login__Segment}`}>
                <h6>{loginState.message}</h6>
              </div>
            )}

            <div className={`${styles.App__Login__Segment}`}>
              <h5
                onClick={() =>
                  setLoginState({
                    state: "signup",
                    message: null,
                  })
                }
              >
                Sign up for a new account
              </h5>
            </div>
          </div>
        )}

        {loginState.state === "confirm" && !isLoading && (
          <div className={`${styles.App__Login}`}>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="email"
                element="input"
                type="text"
                placeholder="Email"
                variant="primary"
                validators={[VALIDATOR_REQUIRE(), VALIDATOR_EMAIL()]}
                onInput={confirmationInputHandler}
                value={confirmationFormState.inputs.email.value}
                valid={confirmationFormState.inputs.email.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <Input
                id="confirmationCode"
                element="input"
                placeholder="Confirmation Code"
                variant="primary"
                validators={[VALIDATOR_REQUIRE()]}
                onInput={confirmationInputHandler}
                value={confirmationFormState.inputs.confirmationCode.value}
                valid={confirmationFormState.inputs.confirmationCode.isValid}
              />
            </div>
            <div className={`${styles.App__Login__Segment}`}>
              <div className={`${styles.App__Login__Button}`}>
                <Button
                  onClick={handleSignUpConfirmation}
                  disabled={!confirmationFormState.isValid}
                >
                  {isActionLoading === true ? (
                    <img
                      style={{width: "20px", height: "20px"}}
                      src="./images/three-dots.svg"
                      alt="Loading..."
                    />
                  ) : (
                    "CONFIRM"
                  )}
                </Button>
              </div>
              <div className={`${styles.App__Login__Button}`}>
                <Button
                  onClick={() =>
                    setLoginState({
                      state: "login",
                      message: null,
                    })
                  }
                >
                  LOGIN
                </Button>
              </div>
            </div>

            {loginState.message !== null && (
              <div className={`${styles.App__Login__Segment}`}>
                <h6>{loginState.message}</h6>
              </div>
            )}
          </div>
        )}

        {loginState.state === "logged_in" && !isLoading && (
          <div className={`${styles.App__Dashboard__Container}`}>
            <Dashboard />
          </div>
        )}

        {(isLoading || loginState.state === null) && (
          <div
            className="absolute top-[10%] inset-0 flex items-center justify-center bg-gray-500 bg-opacity-40 backdrop-blur-md z-50 transition-opacity duration-500 opacity-100"
            style={{
              animation: "fadeIn 0.5s ease-out forwards",
            }}
          >
            <style>
              {`
                        @keyframes fadeIn {
                            from { opacity: 0; }
                            to { opacity: 1; }
                        }
                        .font-inter {
                            font-family: 'Inter', sans-serif;
                        }
                        /* Bouncing dots animation */
                        @keyframes bounce {
                            0%, 80%, 100% {
                                transform: translateY(0);
                            }
                            40% {
                                transform: translateY(-10px);
                            }
                        }

                        .dot {
                            animation: bounce 1s infinite ease-in-out both;
                        }

                        .dot-1 {
                            animation-delay: -0.32s;
                        }
                        .dot-2 {
                            animation-delay: -0.16s;
                        }
                        .dot-3 {
                            animation-delay: 0s;
                        }
                        `}
            </style>
            <div className="flex flex-col items-center">
              <div className="flex space-x-2">
                <div className="dot dot-1 w-4 h-4 bg-white rounded-full"></div>
                <div className="dot dot-2 w-4 h-4 bg-white rounded-full"></div>
                <div className="dot dot-3 w-4 h-4 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default App;
